import { type CreateOfferResponse } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logError } from "@src/appV2/lib/analytics";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { computeShiftPay } from "../../Double/computeDoubleShiftPays";
import { isOfferFreshForShift } from "../../Open/isOfferValidForShift";
import { getOfferCacheKey } from "../../Open/useCreateOffer";
import { type BundleShiftPay, type ShiftBundle } from "../types";
import { toCreateBatchOfferBody, useCreateBatchOffers } from "./useCreateBatchOffers";
import { useLogMissingBundleShiftPays } from "./useLogMissingBundleShiftPays";

const EMPTY_BUNDLE_SHIFT_PAYS = new Map<string, BundleShiftPay>();
const EMPTY_OFFERS_ID_MAP = new Map<string, string>();
const DISABLED_RESULT = {
  bundleShiftPays: EMPTY_BUNDLE_SHIFT_PAYS,
  offersIdMap: EMPTY_OFFERS_ID_MAP,
  isLoading: false,
} as const;

interface UseBundleOffersProps {
  bundle: ShiftBundle | undefined;
  requiresLunchBreak: boolean;
}

/**
 * Hook to fetch offers for each shift in bundle.
 * Returns maps of shiftId to pay and offerId.
 */
export function useBundleOffers(props: UseBundleOffersProps) {
  const { bundle, requiresLunchBreak } = props;
  const queryClient = useQueryClient();

  const shifts = bundle?.shifts ?? [];
  const enabled = isDefined(bundle);
  const shiftIds = shifts.map((shift) => shift.id);
  // Including start and end in the key since offer/pay may depend on them
  const shiftsKey = shifts
    .map((shift) => `${shift.id}:${shift.attributes.start}:${shift.attributes.end}`)
    .sort()
    .join(",");

  const { mutate } = useCreateBatchOffers();
  const [offersMap, setOffersMap] = useState<Map<string, CreateOfferResponse>>(new Map());
  const [hasFetchedOffers, setHasFetchedOffers] = useState(false);

  useEffect(() => {
    if (!enabled || shiftsKey === "") {
      return;
    }

    const cachedOffersMap = new Map<string, CreateOfferResponse>();
    const uncachedIds: string[] = [];

    for (const shiftId of shiftIds) {
      const existing = queryClient.getQueryData<CreateOfferResponse>(getOfferCacheKey(shiftId));
      if (isDefined(existing) && isOfferFreshForShift({ shiftId, offer: existing.data })) {
        cachedOffersMap.set(shiftId, existing);
      } else {
        uncachedIds.push(shiftId);
      }
    }

    if (uncachedIds.length === 0) {
      setOffersMap(cachedOffersMap);
      setHasFetchedOffers(true);
      return;
    }

    setHasFetchedOffers(false);
    mutate(toCreateBatchOfferBody(uncachedIds), {
      onSuccess: (result) => {
        const mergedOffersMap = new Map(cachedOffersMap);
        for (const offer of result.data) {
          const shiftId = offer.relationships?.shift?.data?.id;
          if (isDefined(shiftId)) {
            mergedOffersMap.set(shiftId, { data: offer });
          }
        }
        setOffersMap(mergedOffersMap);
      },
      onError: (error) => {
        logError(APP_V2_APP_EVENTS.SHIFT_BUNDLE_GET_BATCH_OFFERS_FAILURE, { error });
      },
      onSettled: () => {
        setHasFetchedOffers(true);
      },
    });
    // shifts is replaced by shiftsKey for stable memoisation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, shiftsKey, queryClient, mutate]);

  const isLoading = enabled && !hasFetchedOffers;

  const { bundleShiftPays, offersIdMap } = useMemo(
    () => {
      const bundlePays = new Map<string, BundleShiftPay>();
      const offers = new Map<string, string>();

      for (const bundleShift of shifts) {
        const offerResponse = offersMap.get(bundleShift.id);
        if (!isDefined(offerResponse)) {
          continue;
        }

        const shiftPay = computeShiftPay(
          {
            start: bundleShift.attributes.start,
            end: bundleShift.attributes.end,
          },
          offerResponse.data.attributes.pay.amountInMinorUnits,
          requiresLunchBreak
        );

        offers.set(bundleShift.id, offerResponse.data.id);
        bundlePays.set(bundleShift.id, shiftPay);
      }

      return { bundleShiftPays: bundlePays, offersIdMap: offers };
    },
    // shifts is replaced by shiftsKey for stable memoisation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [offersMap, shiftsKey, requiresLunchBreak]
  );

  useLogMissingBundleShiftPays({ shifts, bundleShiftPays, isLoading });

  if (!enabled) {
    return DISABLED_RESULT;
  }

  return { bundleShiftPays, offersIdMap, isLoading };
}
